import { useMassActionsContext } from '@components/MassActions/contexts/MassActions.context';
import { useMemo, useRef, useState } from 'react';
import { isParent } from '../../../../../components/ListV2/components/table/virtualizedTable.utils';
import { Action, ActionHandler } from '../../../../../components/MultipleActionsInterfaces';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import useSearchParams from '../../../../../hooks/useSearchParams';
import LangEnum from '../../../../../lang/LangEnum';
import { useAppEnvironmentStore } from '../../../../../stores/AppEnvironment/appEnvironment.store';
import usePaymentReleaseSettings from '../../../../../stores/GeneralSettings/hooks/usePaymentReleaseSettings';
import { useApplicationContext } from '../../../../context/ApplicationContext/ApplicationContext';
import { UsedInOfflineMode } from '../../../../offline/types/UsedInOfflineMode';
import { useTranslation } from '../../../../translation/TranslationContext';
import { useAbility } from '../../../../users/AbilityContext';
import { OrderActions } from '../../../actions/OrderActions';
import { CopyOrderActionType } from '../../../actions/types/orderActions.types';
import {
  COPY_ACTION_BY_MASS_ACTION,
  COPY_ORDERS_MASS_ACTIONS,
  OrderListMassActionsEnum,
  orderListMassActions,
} from '../../../actions/utils/orderListMassActions';
import {
  canShowOrderListMassActionByRoleOrAbility,
  formatSelectedOrder,
  getCorrectedMassActions,
} from '../../../actions/utils/orderListMassActions.utils';
import { useOrderActionsContext } from '../../../contexts/OrderActions.context';
import { ExportOrderScope } from '../../../export/V2/ExportOrderModal.types';
import { createExportOrderScopeFromBatch } from '../../../export/V2/services/exportOrderScope';
import { BatchUpdateOrdersParams } from '../../../interfaces/orderBatchRepository.interfaces';
import { OrderListWithBaseItem } from '../../../order.types';
import { formatOrderFilters } from '../../../orderFilter/orderFilter.utils';
import { confirmOrderSplitModal } from '../../../utils/confirmOrderSplitModal.utils';
import { OrderListTabName, getOrderStatusByTab } from '../../orderListTabs';
import OrderListMassActionsAlertContent from '../components/OrderListMassActionsAlertContent/OrderListMassActionsAlertContent';
import OrderMassActionsFactory from '../factory/OrderMassActionsFactory';
import {
  BatchChangeOrderStatusByActionType,
  SelectedOrder,
  SelectedOrderWithActions,
} from '../types/orderListMassActions.types';
import useHandleOrderListMassActions from './useHandleOrderListMassActions/useHandleOrderListMassActions';

interface UseOrderListMassActionsParams extends UsedInOfflineMode {
  listItems: OrderListWithBaseItem[];
  selectedIds: string[];
  selectedTab: OrderListTabName;
  isArchivedOrderFilterActive?: boolean;
}

const useOrderListMassActions = ({
  listItems,
  selectedIds,
  selectedTab,
  isOfflineMode,
  isArchivedOrderFilterActive = false,
}: UseOrderListMassActionsParams) => {
  const {
    isBrand,
    isStore,
    brandId,
    userRole,
    hasIntegrations,
    hasOrderIntegration,
    erpIntegration,
  } = useApplicationContext();
  const { reloadCurrent } = useOrderActionsContext();
  const { translate } = useTranslation();
  const { massActionFilter } = useMassActionsContext();
  const { isUpdateOrderOnApproveOrderMassActionEnabled } = useFeatureFlag();
  const appEnvironment = useAppEnvironmentStore((state) => state.appEnvironment);
  const { requirePaymentReleaseForImmediate, requirePaymentReleaseForPreOrder, refundLimitHours } =
    usePaymentReleaseSettings();

  const currentMassAction = useRef<Action<OrderListMassActionsEnum>>();
  const [openMassActionsAlertModal, setOpenMassActionsAlertModal] = useState(false);
  const [openMassActionExportModal, setOpenMassActionExportModal] = useState(false);
  const { searchParams } = useSearchParams();
  const [updateConditionsOnStatusChange, setUpdateConditionsOnStatusChange] = useState(
    isUpdateOrderOnApproveOrderMassActionEnabled,
  );
  const [exportScope, setExportScope] = useState<ExportOrderScope>();
  const [copyOrdersBatchType, setCopyOrdersBatchType] = useState<CopyOrderActionType>();
  const userAbility = useAbility();

  const batchUpdateOrdersParams = useMemo<BatchUpdateOrdersParams>(
    () => ({
      filter: massActionFilter
        ? { status: getOrderStatusByTab(selectedTab), ...formatOrderFilters(searchParams) }
        : undefined,
      orderIds: selectedIds,
      allOrdersFromMassActions: !!massActionFilter,
    }),
    [massActionFilter, searchParams, selectedIds, selectedTab],
  );

  const selectedOrders: (SelectedOrder | undefined)[] = useMemo(
    () =>
      selectedIds.map((id) => {
        let selectedOrder: SelectedOrder | undefined;
        listItems.forEach((listItem, index) => {
          if (selectedOrder) {
            return;
          }

          const itemIsParent = isParent(listItem, listItems[index]);
          if (itemIsParent) {
            const found = listItems?.find(({ orderId }) => orderId === id);
            selectedOrder = found ? formatSelectedOrder(found) : undefined;
          } else if (!itemIsParent && listItem.orderId === id) {
            selectedOrder = formatSelectedOrder(listItem);
          }
        });
        return selectedOrder;
      }),
    [listItems, selectedIds],
  );

  const orderActions = new OrderActions();
  const ordersWithActions: SelectedOrderWithActions[] = [];

  selectedOrders?.forEach((order) => {
    if (order) {
      const actions = orderActions.getMassActions({
        status: order.orderStatus,
        role: userRole,
        hasIntegrations,
        synchronized: order.orderSynchronized,
        hasSplit: order.orderHasSplit,
        splitted: order.orderSplitted,
        archivedOrder: order.orderArchived,
        triggerIntegrationConfig: erpIntegration?.integrationTrigger,
        hasOrderIntegration,
        userAbility,
        lastGatewayPaymentStatus: order.lastGatewayPaymentStatus,
        lastGatewayPaymentUpdatedAt: order.lastGatewayPaymentUpdatedAt,
        orderPaymentStatus: order.orderPaymentStatus,
        orderType: order.orderType,
        requirePaymentGateway: order.requirePaymentGateway,
        requirePaymentReleaseForImmediate,
        requirePaymentReleaseForPreOrder,
        refundLimitHours,
        isArchivedOrderFilterActive,
        appEnvironment,
      });
      ordersWithActions.push({ orderId: order.orderId, actions });
    }
  });

  const massActions: Action<OrderListMassActionsEnum>[] = [];
  const allMassActions = Object.values(orderListMassActions);

  if (!!massActionFilter) {
    const allActionsCorrected = getCorrectedMassActions({
      allMassActions,
      isBrand,
      isOfflineMode,
      userAbility,
      userRole,
      appEnvironment,
    });
    massActions.push(...allActionsCorrected);
  } else if (ordersWithActions.length > 0) {
    const factory = new OrderMassActionsFactory(ordersWithActions);
    allMassActions.forEach((action) => {
      const canShowByRoleOrAbility = canShowOrderListMassActionByRoleOrAbility({
        action,
        userAbility,
        userRole,
      });
      if (!canShowByRoleOrAbility) {
        return;
      }
      const massAction = factory.create(action.type);
      const addAction = massAction && massAction.mustShowAction();
      if (addAction) {
        massActions.push(action);
      }
    });
  }

  if (isStore && !brandId) {
    COPY_ORDERS_MASS_ACTIONS.forEach((copyAction) => {
      const copyOrdersIndex = massActions.findIndex((action) => action.type === copyAction);
      if (copyOrdersIndex !== -1) {
        massActions.splice(copyOrdersIndex, 1);
      }
    });
  }

  const {
    handleSplitOrders,
    handleChangeStatus,
    handleUpdatePrices,
    handleArchiveOrders,
    handleUpdateConditions,
  } = useHandleOrderListMassActions({
    batchUpdateOrdersParams,
  });

  const handleClickMassAction: ActionHandler<OrderListMassActionsEnum> = async (
    massAction: Action<OrderListMassActionsEnum>,
  ) => {
    if (COPY_ORDERS_MASS_ACTIONS.includes(massAction.type)) {
      setCopyOrdersBatchType(COPY_ACTION_BY_MASS_ACTION[massAction.type]);
      return;
    } else if (massAction.type === OrderListMassActionsEnum.EXPORT_MULTIPLE_ORDERS) {
      const exportOrdersScope = createExportOrderScopeFromBatch(batchUpdateOrdersParams);
      setOpenMassActionExportModal(true);
      setExportScope({ ...exportOrdersScope, selectedTab });
      return;
    } else {
      if (massAction.type !== OrderListMassActionsEnum.APPROVE_ORDERS) {
        setUpdateConditionsOnStatusChange(false);
      }
      if (massAction.type === OrderListMassActionsEnum.SPLIT_ORDERS) {
        currentMassAction.current = massAction;
        confirmOrderSplitModal({
          isMassAction: true,
          onConfirm: handleConfirmMassAction,
        });
        return;
      }
      setOpenMassActionsAlertModal(true);
      currentMassAction.current = massAction;
    }
  };

  const executeMassAction = async (action: OrderListMassActionsEnum) => {
    const changeStatus = (customActionType?: BatchChangeOrderStatusByActionType) =>
      handleChangeStatus({
        action,
        updateConditionsAndPrices: updateConditionsOnStatusChange,
        isUpdateConditionsOnStatusChangeEnabled: isUpdateOrderOnApproveOrderMassActionEnabled,
        customActionType,
      });

    switch (action) {
      case OrderListMassActionsEnum.MOVE_TO_SENT:
        return changeStatus('move-to-sent');
      case OrderListMassActionsEnum.CANCEL_ORDERS:
        return changeStatus('move-to-canceled');
      case OrderListMassActionsEnum.APPROVE_ORDERS:
      case OrderListMassActionsEnum.BACK_TO_APPROVED:
        return changeStatus('move-to-approved');
      case OrderListMassActionsEnum.BACK_TO_DRAFT:
      case OrderListMassActionsEnum.FINISH_TO_IN_REVISION:
      case OrderListMassActionsEnum.BACK_TO_IN_REVISION:
      case OrderListMassActionsEnum.FINISH_TO_ON_APPROVAL:
      case OrderListMassActionsEnum.BACK_TO_ON_APPROVAL:
        return changeStatus();
      case OrderListMassActionsEnum.ARCHIVE_ORDERS:
        return handleArchiveOrders(true);
      case OrderListMassActionsEnum.UNARCHIVE_ORDERS:
        return handleArchiveOrders(false);
      case OrderListMassActionsEnum.UPDATE_PRICES:
        return handleUpdatePrices();
      case OrderListMassActionsEnum.UPDATE_CONDITIONS:
        return handleUpdateConditions();
      case OrderListMassActionsEnum.SPLIT_ORDERS:
        return handleSplitOrders();
      case OrderListMassActionsEnum.COPY_ORDERS:
      case OrderListMassActionsEnum.COPY_ORDERS_AS_SUGGESTED:
      case OrderListMassActionsEnum.EXPORT_MULTIPLE_ORDERS:
        throw new Error(`Ação em massa ${action} não precisa de confirmação`);
      default:
        throw new Error(`Ação em massa ${action} não encontrada`);
    }
  };

  const handleConfirmMassAction = async () => {
    const action = currentMassAction.current?.type;
    if (!action) {
      return;
    }

    try {
      const massActionSuccess = await executeMassAction(action);
      if (massActionSuccess.ok) {
        reloadCurrent();
      }
    } catch (_error) {
      return;
    } finally {
      setOpenMassActionsAlertModal(false);
    }
  };

  const handleCloseMassActionsAlertModal = () => {
    setOpenMassActionsAlertModal(false);
  };

  const renderCustomAlertContent = () => {
    if (!currentMassAction.current) {
      return null;
    }
    const action = currentMassAction.current;
    const cannotRenderContentIfUpdateConditionsOnStatusChangeIsDisabled =
      action.type === OrderListMassActionsEnum.APPROVE_ORDERS &&
      !isUpdateOrderOnApproveOrderMassActionEnabled;

    if (cannotRenderContentIfUpdateConditionsOnStatusChangeIsDisabled) {
      return null;
    }

    const onChangeUpdateConditionsOnStatusChange = (shouldUpdate: boolean) => {
      setUpdateConditionsOnStatusChange(shouldUpdate);
    };

    switch (action.type) {
      case OrderListMassActionsEnum.APPROVE_ORDERS:
        return (
          <OrderListMassActionsAlertContent
            action={action}
            updateConditionsOnStatusChange={updateConditionsOnStatusChange}
            onChangeUpdateConditionsOnStatusChange={onChangeUpdateConditionsOnStatusChange}
          />
        );
      default:
        return null;
    }
  };

  const getModalAcceptLabel = () => {
    if (!currentMassAction.current) {
      return undefined;
    }

    const modalAcceptLabel = currentMassAction.current.confirmModalParams?.acceptLabel;
    if (modalAcceptLabel === LangEnum.GLOBAL_APPROVE && updateConditionsOnStatusChange) {
      return LangEnum.GLOBAL_UPDATE_AND_APPROVE;
    }

    return modalAcceptLabel;
  };

  const label = currentMassAction.current?.confirmModalParams?.label
    ? currentMassAction.current.confirmModalParams?.label
    : currentMassAction.current?.label;
  const massActionLabel = label ? translate(label as LangEnum) : '';
  const massActionAcceptLabel = getModalAcceptLabel();
  const massActionDenyLabel = currentMassAction.current?.confirmModalParams?.denyLabel;

  return {
    massActions,
    exportScope,
    massActionLabel,
    massActionDenyLabel,
    massActionAcceptLabel,
    handleClickMassAction,
    handleConfirmMassAction,
    renderCustomAlertContent,
    openMassActionsAlertModal,
    openMassActionExportModal,
    setOpenMassActionExportModal,
    handleCloseMassActionsAlertModal,
    copyOrdersBatchType,
    setCopyOrdersBatchType,
    batchUpdateOrdersParams,
  };
};

export default useOrderListMassActions;
